import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import {
  Typography, Container, Box,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Card, CardContent, CardActions, Grid, Chip, Button,
  Switch, FormControlLabel, IconButton, Tooltip, CircularProgress,
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  useTheme, useMediaQuery, CssBaseline, ThemeProvider, Snackbar, Alert
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  BugReport as BugReportIcon,
  Extension as ExtensionIcon,
  Computer as ComputerIcon
} from '@mui/icons-material';
import { api } from './config';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Modules from './pages/Modules';
import Devices from './pages/Devices';
import theme from './theme';

const drawerWidth = 240;

// Component for the Issues page
function IssuesPage() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const theme = useTheme();

  // Fetch issues from the API
  const fetchIssues = async () => {
    setLoading(true);
    try {
      console.log('Fetching issues');
      const response = await api.get('/issues');
      setIssues(response.data);
      setError(null);
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Error fetching issues: ' + err.message,
        severity: 'error'
      });
      console.error('Error fetching issues:', err);
      setError('Failed to fetch issues. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle toggling issue ignored status
  const handleToggleIgnore = async (issueId) => {
    try {
      const response = await api.post(`/issues/${issueId}/toggle-ignore`);
      if (response.data.success) {
        // Update the issues list with the new ignored status
        setIssues(issues.map(issue =>
          issue.id === issueId ? { ...issue, ignored: response.data.ignored } : issue
        ));
      }
    } catch (err) {
      console.error('Error toggling issue ignored status:', err);
    }
  };

  // Load issues on component mount
  useEffect(() => {
    fetchIssues();
  }, []);

  // Get appropriate color for module type
  const getModuleColor = (moduleId) => {
    const colors = {
      'security_scanner': 'error',
      'performance_analyzer': 'warning',
      'cost_optimizer': 'info',
      'compliance_checker': 'secondary'
    };
    return colors[moduleId] || 'default';
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: 'background.paper',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 500 }}>
            Issues
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={viewMode === 'card'}
                  onChange={() => setViewMode(viewMode === 'table' ? 'card' : 'table')}
                  color="primary"
                />
              }
              label="Card View"
            />
            <Tooltip title="Refresh issues">
              <IconButton
                onClick={fetchIssues}
                color="primary"
                sx={{
                  transition: theme.transitions.create(['transform']),
                  '&:hover': {
                    transform: 'rotate(180deg)',
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Paper
            sx={{
              p: 3,
              backgroundColor: 'error.light',
              color: 'error.contrastText',
              borderRadius: 2,
            }}
          >
            <Typography>{error}</Typography>
          </Paper>
        ) : (
          <>
            {viewMode === 'table' && (
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: theme.transitions.create(['box-shadow']),
                  '&:hover': {
                    boxShadow: theme.shadows[4],
                  },
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Module</TableCell>
                      <TableCell>Check</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {issues.map((issue) => (
                      <TableRow
                        key={issue.id}
                        sx={{
                          backgroundColor: issue.ignored ? 'action.hover' : 'inherit',
                          transition: theme.transitions.create(['background-color']),
                          '&:hover': {
                            backgroundColor: 'action.selected',
                          },
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={issue.module_id.replace('_', ' ')}
                            color={getModuleColor(issue.module_id)}
                            size="small"
                            sx={{ borderRadius: 1 }}
                          />
                        </TableCell>
                        <TableCell>{issue.check_id.replace('_', ' ')}</TableCell>
                        <TableCell>{issue.description}</TableCell>
                        <TableCell>
                          {issue.ignored ? (
                            <Chip
                              icon={<BlockIcon />}
                              label="Ignored"
                              size="small"
                              variant="outlined"
                              sx={{ borderRadius: 1 }}
                            />
                          ) : (
                            <Chip
                              icon={<WarningIcon />}
                              label="Active"
                              size="small"
                              color="warning"
                              sx={{ borderRadius: 1 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={() => handleToggleIgnore(issue.id)}
                            color={issue.ignored ? "primary" : "warning"}
                            variant="contained"
                            sx={{ borderRadius: 1 }}
                          >
                            {issue.ignored ? "Unignore" : "Ignore"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {viewMode === 'card' && (
              <Grid container spacing={3}>
                {issues.map((issue) => (
                  <Grid item xs={12} sm={6} md={4} key={issue.id}>
                    <Card
                      sx={{
                        opacity: issue.ignored ? 0.7 : 1,
                        transition: theme.transitions.create(['transform', 'box-shadow']),
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: theme.shadows[8],
                        },
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Chip
                            label={issue.module_id.replace('_', ' ')}
                            color={getModuleColor(issue.module_id)}
                            size="small"
                            sx={{ borderRadius: 1 }}
                          />
                          {issue.ignored ? (
                            <Chip
                              icon={<BlockIcon />}
                              label="Ignored"
                              size="small"
                              variant="outlined"
                              sx={{ borderRadius: 1 }}
                            />
                          ) : (
                            <Chip
                              icon={<WarningIcon />}
                              label="Active"
                              size="small"
                              color="warning"
                              sx={{ borderRadius: 1 }}
                            />
                          )}
                        </Box>
                        <Typography variant="h6" component="div" sx={{ mb: 1 }}>
                          {issue.check_id.replace('_', ' ')}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {issue.description}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          onClick={() => handleToggleIgnore(issue.id)}
                          color={issue.ignored ? "primary" : "warning"}
                          variant="contained"
                          fullWidth
                          sx={{ borderRadius: 1 }}
                        >
                          {issue.ignored ? "Unignore" : "Ignore"}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

const AppContent = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Issues', icon: <BugReportIcon />, path: '/issues' },
    { text: 'Devices', icon: <ComputerIcon />, path: '/devices' },
    { text: 'Modules', icon: <ExtensionIcon />, path: '/modules' }
  ];

  const drawer = (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}>
      <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderBottom: '1px solid #D0D5DD'
      }}>
        <Box
          component="img"
          src="/assets/sentinel_logo.png"
          alt="Sentinel Logo"
          sx={{
            height: 32,
            width: 'auto',
          }}
        />
        <Typography variant="h6" noWrap component="div" sx={{ color: '#1D2939' }}>
          Sentinel
        </Typography>
      </Box>
      <List sx={{ flexGrow: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              onClick={() => isMobile && handleDrawerToggle()}
              sx={{
                color: '#1D2939',
                '&.Mui-selected': {
                  backgroundColor: '#D8E5EC',
                  color: '#1D2939',
                  '&:hover': {
                    backgroundColor: '#D8E5EC',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(0, 76, 104, 0.08)',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ p: 2, borderTop: '1px solid #D0D5DD' }}>
        <Button
          fullWidth
          startIcon={<LogoutIcon />}
          onClick={logout}
          sx={{
            color: '#1D2939',
            justifyContent: 'flex-start',
            '&:hover': {
              backgroundColor: 'rgba(0, 76, 104, 0.08)',
            },
          }}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );

  if (!user) {
    return <Login />;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/issues" element={<IssuesPage />} />
          <Route path="/devices" element={<Devices />} />
          <Route path="/modules" element={<Modules />} />
        </Routes>
      </Box>
    </Box>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <GoogleOAuthProvider clientId="636320107036-cngdftn5oqj32rkhu2e52r6dv0bf3usq.apps.googleusercontent.com">
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </GoogleOAuthProvider>
    </ThemeProvider>
  );
};

export default App;